// ── Payment History's windows ──────────────────────────────────────
//
// MOVED OUT OF app.js (standing rule ③: extract what you touch, and MOVE and
// CHANGE are separate commits), app.js's own `clients` array read through
// clientsList(): the same window.* names Payment History's buttons call
// (Delete, the receipt, Resend, Edit), and showReceiptHtmlModal, which
// Settings' receipt preview also opens.
//
// THE SOFTER LOOK (Clayton, 2026-10-15: "the payment history section ... needs
// to be updated to our new soft design. along with the pop ups, calendar
// picker, etc."). Each window is the Change billing window's (change-billing.css,
// the .cb kit), its dates are the CRM's own calendar (date-picker.js), and a
// refusal shows in the window, which stays open with what was typed. What each
// one does is unchanged: Delete takes the row out and its amount off Total
// collected, Edit changes the row and moves Total collected by the difference,
// the receipt window shows the stored email with its delivery status, Resend
// sends the same receipt again.

import { escHtml, showToast, formatCurrency } from './utils.js';
import { callFunction } from './api.js';
import { updateClient } from './clients.js';
import { upgradeDates } from './date-picker.js';

/** @type {{ getClients: () => any[] }} */
let _deps = { getClients: () => [] };

/** @param {Partial<typeof _deps>} d */
export function setPaymentEntryDeps(d) { _deps = { ..._deps, ...d }; }

/** app.js's own roster array (the VISIBLE set, not state.js's). */
const clientsList = () => _deps.getClients() || [];

/** @param {any} c */
const nameOf = (c) => c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'this client';

/** The open windows' Escape listeners, by window id, so opening one again never leaves the old listener behind. */
const _offs = new Map();

/**
 * One window in the Change billing window's look: a title with the client under it, a body, the buttons at the
 * bottom. Escape and the × close the window on top only, never while it is saving. It shows once change-billing.css
 * is there, so it never draws without its dialog box (as the Paid another way window does).
 * @param {{ id: string, title: string, subHtml?: string, body: string, buttons: string, wide?: boolean }} o
 *   subHtml: already escaped
 */
function softWindow({ id, title, subHtml = '', body, buttons, wide = false }) {
  const opener = /** @type {HTMLElement|null} */ (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  _offs.get(id)?.();
  document.getElementById(id)?.remove();
  const overlay = document.createElement('div');
  overlay.id = id;
  overlay.className = 'modal-overlay cb pew';
  overlay.style.visibility = 'hidden';
  overlay.innerHTML = `<div class="cb-dialog${wide ? ' cb-wide' : ''}" role="dialog" aria-modal="true" aria-labelledby="${id}-title">
      <div class="cb-dlg-h"><div><h2 class="cb-dlg-title" id="${id}-title">${escHtml(title)}</h2>${subHtml ? `<p class="cb-dlg-sub">${subHtml}</p>` : ''}</div>
        <button type="button" class="cb-x" data-pew="close" aria-label="Close">&times;</button></div>
      <div class="cb-dlg-b">${body}<p class="cb-err" role="alert" hidden></p></div>
      <div class="cb-dlg-f">${buttons}</div>
    </div>`;
  document.body.appendChild(overlay);
  const box = /** @type {HTMLElement} */ (overlay.firstElementChild);
  upgradeDates(box);
  let busy = false;
  const errEl = /** @type {HTMLElement} */ (box.querySelector('.cb-err'));

  const close = () => {
    _offs.get(id)?.();
    overlay.remove();
    if (opener && opener.isConnected) opener.focus();
  };
  /** @param {KeyboardEvent} e */
  const onKey = (e) => {
    if (e.key !== 'Escape' || busy) return;
    const open = [...document.querySelectorAll('.modal-overlay:not(.hidden)')];
    if (open[open.length - 1] === overlay) close();
  };
  document.addEventListener('keydown', onKey);
  _offs.set(id, () => { document.removeEventListener('keydown', onKey); _offs.delete(id); });
  box.addEventListener('click', (e) => {
    const b = /** @type {HTMLElement} */ (e.target).closest('[data-pew="close"]');
    if (b && !busy) close();
  });

  void import('./change-billing/billing-card.js').then((m) => m.ensureChangeBillingCss()).catch(() => {})
    .then(() => {
      overlay.style.visibility = '';
      // The first box to fill (the calendar's box stands for its hidden input), else the window's main button
      const first = [...box.querySelectorAll('input, .dp-trigger, [data-pew-main]')].find((el) => /** @type {HTMLElement} */ (el).offsetParent !== null);
      /** @type {HTMLElement|undefined} */ (first)?.focus();
    });

  return {
    box,
    close,
    /** @param {string} msg */
    fail(msg) { errEl.textContent = msg; errEl.hidden = false; },
    /**
     * Run a save: the buttons are off and Escape waits until it ends. Resolves to whether it worked.
     * @param {() => Promise<void>} work
     */
    async run(work) {
      if (busy) return false;
      busy = true; errEl.hidden = true;
      const btns = /** @type {NodeListOf<HTMLButtonElement>} */ (box.querySelectorAll('button'));
      btns.forEach((x) => { x.disabled = true; });
      try { await work(); return true; }
      finally { busy = false; btns.forEach((x) => { x.disabled = false; }); }
    },
  };
}

/** @param {string} label @param {string} [kind] btn-primary | btn-secondary | btn-danger @param {string} [attrs] */
const button = (label, kind = 'btn-secondary', attrs = '') => `<button type="button" class="${kind} cb-btn"${attrs ? ` ${attrs}` : ''}>${escHtml(label)}</button>`;
const cancel = button('Cancel', 'btn-secondary', 'data-pew="close"');

/** @param {string} clientId @param {string} paymentId @param {number} amount */
window.handleDeletePayment = function(clientId, paymentId, amount) {
  if (!paymentId) return;
  const c = clientsList().find(cl => cl.id === clientId);
  if (!c) return;
  const clientName = escHtml(nameOf(c));
  const amtStr = formatCurrency(amount);
  const w = softWindow({
    id: 'pew-delete',
    title: 'Delete Payment Entry',
    subHtml: clientName,
    body: `<p class="cb-warn">Remove the <strong>${amtStr}</strong> payment entry from <strong>${clientName}</strong>?</p>
      <p class="cb-hint">This will also subtract <strong>${amtStr}</strong> from their total collected. This cannot be undone.</p>`,
    buttons: cancel + button('Delete', 'btn-danger', 'data-pew="ok"'),
  });
  w.box.querySelector('[data-pew="ok"]')?.addEventListener('click', () => {
    void w.run(async () => {
      try {
        const fresh = clientsList().find(cl => cl.id === clientId);
        if (!fresh) throw new Error('Client not found');
        const existingHistory = Array.isArray(fresh.paymentHistory) ? fresh.paymentHistory : [];
        const newHistory = existingHistory.filter(p => p.id !== paymentId);
        const currentCollected = Number(fresh.totalCollected ?? fresh.moneyCollectedUpfront) || 0;
        const newCollected = Math.max(0, parseFloat((currentCollected - amount).toFixed(2)));
        await updateClient(clientId, { paymentHistory: newHistory, totalCollected: newCollected });
        w.close();
        showToast('Payment entry deleted.', 'success');
      } catch (err) {
        w.fail('Error deleting payment: ' + /** @type {any} */ (err).message);
      }
    });
  });
};

/**
 * The rendered receipt in an isolated iframe. Shared by the Settings receipt preview and the client profile's
 * "View receipt" action. srcdoc keeps the email's own CSS sandboxed from the dashboard page.
 * @param {string} html @param {string} title
 * @param {{ statusHtml?: string, resend?: { clientId: string, invoiceId: string, email?: string } }} [extras]
 */
export function showReceiptHtmlModal(html, title, extras = {}) {
  const w = softWindow({
    id: 'receipt-preview-overlay',
    title: title || 'Receipt',
    subHtml: extras.statusHtml || '',
    body: '<iframe class="pew-frame" title="Receipt"></iframe>',
    buttons: (extras.resend ? button('Resend', 'btn-secondary pew-left', 'data-pew="resend"') : '') + button('Close', 'btn-primary', 'data-pew="close" data-pew-main'),
    wide: true,
  });
  /** @type {HTMLIFrameElement} */ (w.box.querySelector('.pew-frame')).srcdoc = html;
  const resend = extras.resend;
  if (resend) w.box.querySelector('[data-pew="resend"]')?.addEventListener('click', () => window.resendStoredReceipt(resend.clientId, resend.invoiceId, resend.email || ''));
}

// Build the delivery-status + resend-history line shown above a stored receipt.
// Icon + text (not color alone) so it reads regardless of color vision.
/** @param {any} data */
function receiptStatusHtml(data) {
  /** @param {string} iso */
  const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
  let main;
  switch (data.deliveryStatus) {
    case 'delivered':  main = `<span style="color:var(--success);font-weight:600;">✓ Delivered</span>${data.deliveryStatusAt ? ' · ' + fmt(data.deliveryStatusAt) : ''}`; break;
    case 'bounced':    main = `<span style="color:var(--warning);font-weight:600;">⚠ Bounced</span>${data.deliveryDetail ? ' · ' + escHtml(data.deliveryDetail) : ''} · try resending to a corrected address`; break;
    case 'complained': main = `<span style="color:var(--warning);font-weight:600;">⚠ Marked as spam</span> · it reached them but landed in spam`; break;
    case 'rejected':   main = `<span style="color:var(--warning);font-weight:600;">⚠ Rejected</span>`; break;
    case 'deferred':   main = `<span style="color:var(--text-muted);">… Delivery delayed, retrying</span>`; break;
    default:           main = `<span style="color:var(--text-muted);">Sent · delivery not yet confirmed</span>`;
  }
  let resend = '';
  if (data.resendCount > 0) {
    resend = ` · Resent ${data.resendCount}×${data.lastResentTo ? ' to ' + escHtml(data.lastResentTo) : ''}${data.lastResentAt ? ' on ' + fmt(data.lastResentAt) : ''}`;
  }
  return main + resend;
}

// Client profile → open the archived receipt email for one payment, with its
// delivery status and a resend action. Falls back to the Stripe hosted invoice
// if no snapshot exists yet (e.g. a payment from before archiving that wasn't
// backfilled). Pass ev=null to refresh silently (e.g. after a resend).
/** @param {Event|null} ev @param {string} clientId @param {string} invoiceId */
window.viewStoredReceipt = async function(ev, clientId, invoiceId) {
  const btn = /** @type {HTMLButtonElement|null} */ (ev?.currentTarget || null);
  const fallbackUrl = btn?.dataset?.receiptUrl ? decodeURIComponent(btn.dataset.receiptUrl) : '';
  if (btn) btn.disabled = true;
  try {
    const data = await callFunction('get-stored-receipt', null, 'GET', { clientId, invoiceId });
    showReceiptHtmlModal(data.html, data.reconstructed ? 'Receipt (reconstructed)' : 'Receipt', {
      statusHtml: receiptStatusHtml(data),
      resend: { clientId, invoiceId, email: data.toEmail || '' },
    });
  } catch (err) {
    if (fallbackUrl) window.open(fallbackUrl, '_blank', 'noopener');
    else showToast('No stored receipt for this payment yet.', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
};

// Resend a stored receipt — same content, to the original or a corrected email.
/** @param {string} clientId @param {string} invoiceId @param {string} defaultEmail */
window.resendStoredReceipt = function(clientId, invoiceId, defaultEmail) {
  const w = softWindow({
    id: 'pew-resend',
    title: 'Resend receipt',
    body: `<form class="pew-form"><div class="cb-field">
       <label class="cb-lbl" for="resend-receipt-email">Send to</label>
       <input type="email" id="resend-receipt-email" class="cb-ctl pew-wide" value="${escHtml(defaultEmail || '')}" placeholder="client@email.com">
       <p class="cb-hint">Sends the exact same receipt again. Change the address if theirs was wrong.</p>
     </div></form>`,
    buttons: cancel + button('Send receipt', 'btn-primary', 'data-pew="ok"'),
  });
  const send = () => w.run(async () => {
    try {
      const email = (/** @type {HTMLInputElement|null} */ (w.box.querySelector('#resend-receipt-email'))?.value || '').trim();
      if (!email || !email.includes('@')) throw new Error('Enter a valid email address');
      const res = await callFunction('resend-receipt', { clientId, invoiceId, email });
      w.close();
      showToast('✓ Receipt resent to ' + res.to, 'success');
      window.viewStoredReceipt(null, clientId, invoiceId);
    } catch (err) {
      w.fail(/** @type {any} */ (err).message || 'Resend failed');
    }
  });
  w.box.querySelector('[data-pew="ok"]')?.addEventListener('click', () => { void send(); });
  w.box.querySelector('form')?.addEventListener('submit', (e) => { e.preventDefault(); void send(); });
};

/** @param {string} clientId @param {string} paymentId */
window.handleEditPayment = function(clientId, paymentId) {
  if (!paymentId) return;
  const c = clientsList().find(cl => cl.id === clientId);
  if (!c) return;
  const history = Array.isArray(c.paymentHistory) ? c.paymentHistory : [];
  const entry = history.find(p => p.id === paymentId);
  if (!entry) { showToast('Payment entry not found.', 'error'); return; }

  const rawDate = entry.date?.split('T')[0] || '';
  const desc = entry.description || '';
  const amt = entry.amount || 0;

  const w = softWindow({
    id: 'pew-edit',
    title: 'Edit Payment Entry',
    subHtml: escHtml(nameOf(c)),
    body: `<form class="pew-form">
       <div class="cb-field"><label class="cb-lbl" for="edit-pay-date">Date</label><input type="date" id="edit-pay-date" value="${escHtml(rawDate)}"></div>
       <div class="cb-field"><label class="cb-lbl" for="edit-pay-desc">Description</label><input type="text" id="edit-pay-desc" class="cb-ctl pew-wide" value="${escHtml(desc)}" placeholder="Payment"></div>
       <div class="cb-field"><label class="cb-lbl" for="edit-pay-amount">Amount</label><span class="cb-amount-wrap cb-is-usd"><span class="cb-amount-u" aria-hidden="true">$</span><input type="number" id="edit-pay-amount" class="cb-ctl cb-amount" value="${amt}" step="0.01" min="0" onwheel="this.blur()"></span></div>
     </form>`,
    buttons: cancel + button('Save', 'btn-primary', 'data-pew="ok"'),
  });
  /** @param {string} sel */
  const val = (sel) => /** @type {HTMLInputElement|null} */ (w.box.querySelector(sel))?.value;
  const save = () => w.run(async () => {
    try {
      const newDate = val('#edit-pay-date');
      const newDesc = val('#edit-pay-desc') || 'Payment';
      const newAmt = parseFloat(val('#edit-pay-amount') || '') || 0;
      if (!newDate) throw new Error('Date is required');
      if (newAmt <= 0) throw new Error('Amount must be positive');

      const fresh = clientsList().find(cl => cl.id === clientId);
      if (!fresh) throw new Error('Client not found');
      const existingHistory = Array.isArray(fresh.paymentHistory) ? [...fresh.paymentHistory] : [];
      const idx = existingHistory.findIndex(p => p.id === paymentId);
      if (idx === -1) throw new Error('Entry not found');

      const oldAmt = existingHistory[idx].amount || 0;
      const delta = newAmt - oldAmt;
      existingHistory[idx] = { ...existingHistory[idx], date: newDate, description: newDesc, amount: newAmt };

      const currentCollected = Number(fresh.totalCollected ?? fresh.moneyCollectedUpfront) || 0;
      const newCollected = Math.max(0, parseFloat((currentCollected + delta).toFixed(2)));

      await updateClient(clientId, { paymentHistory: existingHistory, totalCollected: newCollected });
      w.close();
      showToast('Payment entry updated.', 'success');
    } catch (err) {
      w.fail('Error updating payment: ' + /** @type {any} */ (err).message);
    }
  });
  w.box.querySelector('[data-pew="ok"]')?.addEventListener('click', () => { void save(); });
  w.box.querySelector('form')?.addEventListener('submit', (e) => { e.preventDefault(); void save(); });
};
